import { useQuery } from '@tanstack/react-query';
import { SsoIdentifierEnum } from 'api/enums';
import { onBoardingAuthenticateAutodesk } from 'api/services';
import { BrokenLinkIcon } from 'library/icons';
import { CircularProgress } from 'library/loaders';
import { IllustrationView } from 'modules/shared/features/illustration';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const constructAutodeskRedirectUrl = () => {
    return import.meta.env.VITE_APP_AUTH_REDIRECT_URL + '/autodesk-onboarding-authenticate';
};

const AutodeskIllustration = () => {
    return (
        <div className="flex items-center gap-5">
            <img className="h-10" src="/logos/logo.png" alt="progressCenter" />
            <BrokenLinkIcon />
            <img className="h-10" src="/plugins-images/autodesk.svg" alt="Autodesk" />
        </div>
    );
};

const AutodeskOnboardingAuthenticatePage = () => {
    const [searchParams] = useSearchParams();

    const navigate = useNavigate();

    const code = searchParams.get('code');
    const state = searchParams.get('state');

    const redirectUri = constructAutodeskRedirectUrl();
    const { url, invitationId, invitationEmail } = JSON.parse(decodeURIComponent(state));

    const autodeskQuery = useQuery({
        queryKey: ['autodesk-onboarding-authenticate', code, redirectUri],
        queryFn: () => onBoardingAuthenticateAutodesk({ code, redirectUri }),
        retry: false,
    });

    if (autodeskQuery.isLoading) {
        return (
            <section>
                <div className="fixed top-0 left-0 flex flex-col items-center justify-center h-screen w-full bg-card-color">
                    <div className="flex items-center justify-center w-14 h-14 rounded-lg text-primary ">
                        <CircularProgress size={32} />
                    </div>
                </div>
            </section>
        );
    }

    if (autodeskQuery.data?.email !== invitationEmail) {
        return (
            <IllustrationView
                className="justify-center h-screen"
                illustration={<AutodeskIllustration />}
                title="Autodesk Authentication Failed"
                message={
                    <>
                        This email is not linked to your account.
                        <br /> Please Sign up with the linked account or contact support to update your email.
                        <div className="mt-2">
                            <Link to={url} replace className="text-primary font-medium cursor-pointer">
                                Back to home
                            </Link>
                        </div>
                    </>
                }
            />
        );
    }

    if (autodeskQuery.isSuccess) {
        if (autodeskQuery.data) {
            const redirectUrl = new URL(`/invitations/${invitationId}/form`, url);
            redirectUrl.searchParams.set('user', JSON.stringify(autodeskQuery.data));
            redirectUrl.searchParams.set('ssoType', SsoIdentifierEnum.AUTODESK_SSO);

            window.location.href = redirectUrl.toString();
        } else {
            navigate(url);
        }
    }

    if (autodeskQuery.error) {
        return (
            <IllustrationView
                className="justify-center h-screen"
                illustration={<AutodeskIllustration />}
                title="Autodesk Authentication Failed"
                message={
                    <>
                        We couldn’t connect to your Autodesk account. Please check your credentials or try again.
                        <div className="mt-2">
                            <Link to={url} replace className="text-primary font-medium cursor-pointer">
                                Back to home
                            </Link>
                        </div>
                    </>
                }
            />
        );
    }
};

export default AutodeskOnboardingAuthenticatePage;
